import React from 'react';
import { 
  Container, Grid, Box, Typography, Avatar, Divider, Chip, List, ListItem, ListItemText, ListItemIcon,
  useTheme,
  useMediaQuery
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import CodeIcon from '@mui/icons-material/Code';
import StarIcon from '@mui/icons-material/Star';
import CircleIcon from '@mui/icons-material/Circle';

// --- FRESHER RESUME DATA ---
const resumeData = {
  personalInfo: {
    name: "ALEXANDER PARKER",
    title: "Computer Science Graduate",
    email: "alex.parker@email.com",
    phone: "+1 (234) 567-8901",
    address: "San Francisco, CA",
    profileSummary: "Recent Computer Science graduate with strong foundation in software development, algorithms, and data structures. Passionate about creating innovative solutions and eager to contribute to a dynamic tech team. Quick learner with excellent problem-solving skills and a strong academic record.",
  },
  education: [
    {
      year: "2024",
      degree: "Bachelor of Science in Computer Science",
      institution: "Stanford University",
      details: "Graduated Magna Cum Laude with specialization in Software Engineering",
      gpa: "3.8/4.0",
      relevantCourses: ["Data Structures", "Algorithms", "Web Development", "Database Systems", "Machine Learning", "Software Engineering"]
    },
   
  ],
  projects: [
    {
      title: "E-Commerce Platform",
      duration: "Jan 2024 - May 2024",
      technologies: ["React", "Node.js", "MongoDB", "Express"],
      description: "Full-stack e-commerce application with user authentication, product management, and payment integration",
      features: [
        "Implemented secure user authentication using JWT",
         "Integrated Stripe payment gateway",
        "Designed responsive UI with React and Material-UI",
        "Integrated Stripe payment gateway",
        "Deployed on AWS with CI/CD pipeline",
                "Integrated Stripe payment gateway",

      ]
    },
   
   
  ],
  skills: {
    programming: ["Python", "JavaScript", "Java", "C++", "SQL"],
    frameworks: ["React", "Node.js", "Spring Boot", "Express.js", "Django"],
    tools: ["Git", "Docker", "AWS", "Postman", "Jira", "VS Code"],
    softSkills: ["Problem Solving", "Team Collaboration", "Communication", "Time Management", "Adaptability"]
  },
  
  achievements: [
    "Dean's List - All semesters (2020-2024)",
    "1st Place - University Hackathon 2023",
    "Best Project Award - Software Engineering Course",
    "Published research paper on 'Optimizing Neural Networks'",  "1st Place - University Hackathon 2023",
  ]
};

// --- MAIN RESUME COMPONENT ---
const Resume28 = ({ 
  pageSize = "A4",
  color = '#2196f3' // Default blue color
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const data = resumeData;

  // Color scheme using the provided color
  const primaryColor = color;
  const secondaryColor = '#f5f5f5';
  const textColor = '#333';
  const lightTextColor = '#666';

  return (
    <Container maxWidth="md" sx={{ 
      my: isMobile ? 2 : 0, 
      p: 0, 
      bgcolor: 'white',
      width: pageSize === "A4" ? "210mm" : "216mm",
      maxWidth: '100%',
      mx: 'auto',
      boxShadow: isMobile ? 1 : 3,
      borderRadius: '4px',
      overflow: 'hidden'
    }}>
      
      {/* Main Header */}
      <Box sx={{
        backgroundColor: primaryColor,
        color: 'white',
        p: isMobile ? 2 : 3,
        textAlign: 'center'
      }}>
        <Typography variant="h3" sx={{ 
          fontWeight: '700',
          fontSize: isMobile ? '1.8rem' : '2.2rem',
          letterSpacing: '0.5px',
          mb: 1
        }}>
          {data.personalInfo.name}
        </Typography>
        <Typography variant="h5" sx={{ 
          fontWeight: '300',
          fontSize: isMobile ? '1rem' : '1.2rem',
          letterSpacing: '1px',
          mb: 2
        }}>
          {data.personalInfo.title}
        </Typography>
        
        {/* Contact Info Row */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          flexWrap: 'wrap',
          gap: isMobile ? 1 : 2,
          mt: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EmailIcon sx={{ fontSize: 16, mr: 0.5 }} />
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
              {data.personalInfo.email}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PhoneIcon sx={{ fontSize: 16, mr: 0.5 }} />
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
              {data.personalInfo.phone}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocationOnIcon sx={{ fontSize: 16, mr: 0.5 }} />
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
              {data.personalInfo.address}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Two Column Layout */}
      <Grid container sx={{ flexDirection: isMobile ? 'column' : 'row' }}>
        
        {/* === LEFT COLUMN === */}
        <Grid item xs={12} md={4} sx={{
          p: isMobile ? 2 : 3
        }}>
          
          {/* Profile Summary */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              pb: 1,
              borderBottom: `2px solid ${primaryColor}`
            }}>
              <Typography variant="h6" sx={{ 
                color: primaryColor,
                fontWeight: '700',
                fontSize: '1rem',
                textTransform: 'uppercase'
              }}>
                Profile
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ 
              color: lightTextColor, 
              fontSize: '0.85rem',
              lineHeight: 1.6
            }}>
              {data.personalInfo.profileSummary}
            </Typography>
          </Box>

          {/* Education */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              pb: 1,
              borderBottom: `2px solid ${primaryColor}`
            }}>
              <SchoolIcon sx={{ color: primaryColor, mr: 1, fontSize: 20 }} />
              <Typography variant="h6" sx={{ 
                color: primaryColor,
                fontWeight: '700',
                fontSize: '1rem',
                textTransform: 'uppercase'
              }}>
                Education
              </Typography>
            </Box>
            
            {data.education.map((edu, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ 
                  color: textColor,
                  fontWeight: '600',
                  fontSize: '0.9rem',
                  mb: 0.5
                }}>
                  {edu.degree}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: primaryColor,
                  fontWeight: '500',
                  fontSize: '0.85rem',
                  mb: 0.5
                }}>
                  {edu.institution}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: lightTextColor,
                  fontSize: '0.8rem',
                  fontStyle: 'italic',
                  mb: 0.5
                }}>
                  {edu.year} • GPA: {edu.gpa}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: lightTextColor,
                  fontSize: '0.8rem',
                  mb: 1
                }}>
                  {edu.details}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Skills */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              pb: 1,
              borderBottom: `2px solid ${primaryColor}`
            }}>
              <CodeIcon sx={{ color: primaryColor, mr: 1, fontSize: 20 }} />
              <Typography variant="h6" sx={{ 
                color: primaryColor,
                fontWeight: '700',
                fontSize: '1rem',
                textTransform: 'uppercase'
              }}>
                Skills
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ 
                color: textColor,
                fontWeight: '600',
                fontSize: '0.85rem',
                mb: 1
              }}>
                Technical Skills
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {[...data.skills.programming, ...data.skills.frameworks, ...data.skills.tools].map((skill, index) => (
                  <Chip
                    key={index}
                    label={skill}
                    size="small"
                    sx={{
                      backgroundColor: 'white',
                      border: `1px solid ${primaryColor}`,
                      color: primaryColor,
                      fontSize: '0.75rem',
                      height: '22px'
                    }}
                  />
                ))}
              </Box>
            </Box>
            
         
          </Box>

        </Grid>
        
        {/* === RIGHT COLUMN === */}
        <Grid item xs={12} md={8} sx={{
          p: isMobile ? 2 : 3
        }}>
          
          {/* Projects Section */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 3,
              pb: 1,
              borderBottom: `2px solid ${primaryColor}`
            }}>
              <WorkIcon sx={{ color: primaryColor, mr: 1, fontSize: 22 }} />
              <Typography variant="h5" sx={{ 
                color: textColor,
                fontWeight: '700',
                fontSize: '1.2rem',
                textTransform: 'uppercase'
              }}>
                Academic Projects
              </Typography>
            </Box>

            {data.projects.map((project, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" sx={{ 
                    color: textColor,
                    fontWeight: '700',
                    fontSize: '1rem'
                  }}>
                    {project.title}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: primaryColor,
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    backgroundColor: secondaryColor,
                    px: 1.5,
                    py: 0.3,
                    borderRadius: '3px'
                  }}>
                    {project.duration}
                  </Typography>
                </Box>
                
                <Typography variant="body2" sx={{ 
                  color: lightTextColor,
                  mb: 1.5,
                  fontSize: '0.85rem',
                  lineHeight: 1.6
                }}>
                  {project.description}
                </Typography>
                
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ 
                    color: textColor,
                    fontWeight: '600',
                    fontSize: '0.85rem',
                    mb: 1
                  }}>
                    Technologies Used:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {project.technologies.map((tech, i) => (
                      <Chip
                        key={i}
                        label={tech}
                        size="small"
                        sx={{
                          backgroundColor: secondaryColor,
                          color: textColor,
                          fontSize: '0.75rem',
                          height: '22px'
                        }}
                      />
                    ))}
                  </Box>
                </Box>
                
                <Box>
                  <List dense disablePadding>
                    {project.features.map((feature, i) => (
                      <ListItem key={i} sx={{ py: 0.25, alignItems: 'flex-start' }}>
                        <ListItemIcon sx={{ minWidth: '24px', mt: '2px' }}>
                          <CircleIcon sx={{ fontSize: 6, color: primaryColor }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature} 
                          primaryTypographyProps={{ 
                            color: lightTextColor,
                            fontSize: '0.85rem',
                            lineHeight: 1.5
                          }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
                
                {index < data.projects.length - 1 && (
                  <Divider sx={{ my: 2, borderColor: secondaryColor }} />
                )}
              </Box>
            ))}
          </Box>

      

        </Grid>
      </Grid>
      
      {/* Footer */}
      <Box sx={{ 
        backgroundColor: primaryColor,
        color: 'white',
        p: 1,
        textAlign: 'center'
      }}>
        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
          References available upon request
        </Typography>
      </Box>
    </Container>
  );
};

export default Resume28;

export const resumeMeta = {
  hasPhoto: false,
  columns: 1,
  colorOptions: [
    { value: '#2196f3', label: 'Blue' },
    { value: '#4caf50', label: 'Green' },
    { value: '#ff9800', label: 'Orange' },
    { value: '#9c27b0', label: 'Purple' },
    { value: '#f44336', label: 'Red' }
  ]
};